import { useCallback, useEffect, useRef, useState } from "react";
import type {
  PartnerHomeActivity,
  PartnerHomeRange,
  PartnerHomeResponse,
} from "../../shared/types";
import { useNative } from "../../shared/phone/media";
import { usePartnerAuth } from "../context/PartnerAuthContext";
import { partnerApi, PartnerApiError } from "../lib/api";
import { HomeNative } from "./HomeNative";

/**
 * The partner portal home (node P2): the signed-in partner's OWN link, coupon,
 * KPIs, earnings, and recent activity for the selected program. Every number is
 * real, from GET /api/partner/home (scoped to the session + program). Clicks read
 * an honest 0 until tracking (node S2) records any. Honest empty state when the
 * partner has no activity yet.
 */

const CURRENCY_SYMBOL: Record<string, string> = {
  USD: "$",
  CAD: "$",
  AUD: "$",
  EUR: "€",
  GBP: "£",
};

function money(cents: number, currency: string): string {
  const symbol = CURRENCY_SYMBOL[currency] ?? "";
  const dollars = cents / 100;
  const body = Number.isInteger(dollars)
    ? dollars.toLocaleString("en-US")
    : dollars.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${symbol}${body}`;
}

function count(n: number): string {
  return n.toLocaleString("en-US");
}

const RANGE_OPTIONS: { key: PartnerHomeRange; label: string; sub: string }[] = [
  { key: "7d", label: "Last 7 days", sub: "last 7 days" },
  { key: "30d", label: "Last 30 days", sub: "last 30 days" },
  { key: "90d", label: "Last 90 days", sub: "last 90 days" },
  { key: "month", label: "This month", sub: "this month" },
  { key: "all", label: "All time", sub: "all time" },
];

function rangeMeta(range: PartnerHomeRange) {
  return RANGE_OPTIONS.find((o) => o.key === range) ?? RANGE_OPTIONS[1];
}

function firstLetter(name: string): string {
  return (name.trim()[0] ?? "P").toUpperCase();
}

/** Build the area + line paths for the 6-point earnings chart. */
function chartPaths(cents: number[]): { line: string; area: string; lastX: number; lastY: number } {
  const n = cents.length;
  const x0 = 20;
  const x1 = 300;
  const step = n > 1 ? (x1 - x0) / (n - 1) : 0;
  const max = Math.max(...cents, 1);
  const y = (v: number) => 110 - (v / max) * 90;
  const pts = cents.map((v, i) => ({ x: x0 + i * step, y: y(v) }));
  const line = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const last = pts[pts.length - 1];
  const first = pts[0];
  const area = `${line} L${last.x.toFixed(1)},110 L${first.x.toFixed(1)},110 Z`;
  return { line, area, lastX: last.x, lastY: last.y };
}

function ActivityIcon({ tone }: { tone: PartnerHomeActivity["tone"] }) {
  const cls = tone === "held" ? "ic hold" : tone === "pending" ? "ic acc" : "ic win";
  if (tone === "pending") {
    return (
      <span className={cls} aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <path d="M12 5v14M5 12h14" />
        </svg>
      </span>
    );
  }
  if (tone === "held") {
    return (
      <span className={cls} aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="8" />
          <path d="M12 8v4l2.5 1.6" />
        </svg>
      </span>
    );
  }
  return (
    <span className={cls} aria-hidden="true">
      <svg viewBox="0 0 24 24">
        <path d="M5 12.5l4.2 4.2L19 7" />
      </svg>
    </span>
  );
}

function amountText(a: PartnerHomeActivity, currency: string): { text: string; className: string } {
  if (a.amountCents === null) return { text: "pending", className: "amt dim" };
  const abs = Math.abs(a.amountCents);
  const sign = a.tone === "earned" ? "+" : a.amountCents < 0 ? "-" : "";
  const className = a.tone === "earned" || a.tone === "paid" ? "amt g" : "amt";
  return { text: `${sign}${money(abs, currency)}`, className };
}

export function HomeView() {
  const { currentMembership } = usePartnerAuth();
  const programId = currentMembership?.programId ?? null;

  const [home, setHome] = useState<PartnerHomeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [range, setRange] = useState<PartnerHomeRange>("30d");
  const [dismissed, setDismissed] = useState(false);
  const [drOpen, setDrOpen] = useState(false);
  const [copied, setCopied] = useState<"link" | "coupon" | null>(null);
  const drRef = useRef<HTMLDivElement>(null);
  const native = useNative();

  const load = useCallback(
    async (pid: string, r: PartnerHomeRange) => {
      setError(null);
      try {
        const data = await partnerApi.home(pid, r);
        setHome(data);
      } catch (err) {
        setHome(null);
        setError(
          err instanceof PartnerApiError
            ? err.message
            : "We could not load your home. Try again in a moment.",
        );
      }
    },
    [],
  );

  useEffect(() => {
    if (!programId) return;
    void load(programId, range);
  }, [programId, range, load]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!drRef.current?.contains(e.target as Node)) setDrOpen(false);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  const copy = useCallback(async (value: string, which: "link" | "coupon") => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(which);
      window.setTimeout(() => setCopied((c) => (c === which ? null : c)), 1600);
    } catch {
      // Clipboard can be unavailable (permissions, insecure context); leave as is.
    }
  }, []);

  if (native) {
    return (
      <HomeNative
        home={home}
        error={error}
        range={range}
        onRange={setRange}
        dismissed={dismissed}
        onDismiss={() => setDismissed(true)}
        onRetry={() => programId && void load(programId, range)}
      />
    );
  }

  if (error && !home) {
    return (
      <div className="content">
        <div className="es home-error">
          <div className="es-ic" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="M12 8v5M12 16.5v.5" />
              <circle cx="12" cy="12" r="9" />
            </svg>
          </div>
          <h3>Something went wrong</h3>
          <p>{error}</p>
          <button
            type="button"
            className="btn ghost"
            onClick={() => programId && load(programId, range)}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!home) {
    return (
      <div className="content">
        <div className="home-loading">
          <div className="ps-dot" aria-hidden="true" />
          <span>Loading your home...</span>
        </div>
      </div>
    );
  }

  const meta = rangeMeta(home.range);
  const showBanner = home.status === "pending" && !dismissed;
  const linkDisplay = home.link ? home.link.replace(/^https?:\/\//, "") : null;

  const signupSub =
    home.signupsThisWeek > 0 ? `+${count(home.signupsThisWeek)} this week` : meta.sub;
  const earnedSub =
    home.earnedThisMonthCents > 0
      ? `+${money(home.earnedThisMonthCents, home.currency)} this month`
      : meta.sub;

  const chart = chartPaths(home.earnings.map((p) => p.cents));

  return (
    <div className="content app-view">
      {showBanner && (
        <div className="approval-banner">
          <span className="ab-ic" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="8" />
              <path d="M12 8v4l2.5 1.6" />
            </svg>
          </span>
          <span>
            <b>Pending {home.productName}'s approval.</b> Your link works now. Rewards unlock once
            you are approved.
          </span>
          <button
            type="button"
            className="ab-x"
            aria-label="Dismiss"
            onClick={() => setDismissed(true)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
      )}

      <div className="home-top">
        <div className="daterange" ref={drRef}>
          <button
            type="button"
            className="dr-btn"
            aria-haspopup="true"
            aria-expanded={drOpen}
            onClick={(e) => {
              e.stopPropagation();
              setDrOpen((o) => !o);
            }}
          >
            <span>{meta.label}</span>
            <svg className="chev" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M7 10l5 5 5-5" />
            </svg>
          </button>
          {drOpen && (
            <div className="dr-menu" onClick={(e) => e.stopPropagation()}>
              {RANGE_OPTIONS.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  className={o.key === home.range ? "on" : ""}
                  onClick={() => {
                    setDrOpen(false);
                    setRange(o.key);
                  }}
                >
                  {o.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="promo">
        <div className="plogo" aria-hidden="true">
          {firstLetter(home.productName)}
        </div>
        <div>
          <div className="pn">{home.productName}</div>
          {home.productTagline && <div className="pd">{home.productTagline}</div>}
        </div>
      </div>

      <div className="linkcard">
        <div className="k">Your link</div>
        {linkDisplay ? (
          <div className="linkrow">
            <span className="linkurl">{linkDisplay}</span>
            <button type="button" className="btn" onClick={() => home.link && copy(home.link, "link")}>
              {copied === "link" ? "Copied" : "Copy"}
            </button>
          </div>
        ) : (
          <div className="linkrow">
            <span className="linkurl">{`/go?p=${home.linkSlug}`}</span>
            <span className="link-pending">Activates once {home.productName} sets a destination.</span>
          </div>
        )}

        {home.couponCode && (
          <>
            <div className="k with-note" style={{ marginTop: 16 }}>
              Your coupon
              <span className="note">
                {home.couponDiscountPercent
                  ? `your audience saves ${home.couponDiscountPercent}%, you still earn`
                  : "your audience saves, you still earn"}
              </span>
            </div>
            <div className="linkrow">
              <span className="linkurl coupon">{home.couponCode}</span>
              <button
                type="button"
                className="btn ghost"
                onClick={() => home.couponCode && copy(home.couponCode, "coupon")}
              >
                {copied === "coupon" ? "Copied" : "Copy code"}
              </button>
            </div>
          </>
        )}

        <div className="link-hint">
          When you post, add <b>#ad</b> or <b>paid link</b> so people know you earn a commission.
        </div>
      </div>

      {!home.hasActivity ? (
        <div className="es home-empty">
          <div className="es-ic" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="M4 19V5M4 19h16M8 16l3.5-4 3 2.5L20 8" />
            </svg>
          </div>
          <h3>No activity yet</h3>
          <p>
            Share your link or coupon above. Your clicks, signups and rewards show up here as they
            come in.
          </p>
        </div>
      ) : (
        <>
          <div className="grid kpis">
            <div className="kpi">
              <div className="k">Clicks</div>
              <div className="v">{count(home.kpis.clicks)}</div>
              <div className="t">{meta.sub}</div>
            </div>
            <div className="kpi">
              <div className="k">Signups</div>
              <div className="v">{count(home.kpis.signups)}</div>
              <div className={`t${home.signupsThisWeek > 0 ? " up" : ""}`}>{signupSub}</div>
            </div>
            <div className="kpi">
              <div className="k">Customers</div>
              <div className="v">{count(home.kpis.customers)}</div>
              <div className="t">they pay out</div>
            </div>
            <div className="kpi">
              <div className="k">Earned</div>
              <div className="v">{money(home.kpis.earnedCents, home.currency)}</div>
              <div className={`t${home.earnedThisMonthCents > 0 ? " up" : ""}`}>{earnedSub}</div>
            </div>
          </div>

          <div className="grid home-charts">
            <div className="card">
              <div className="card-h">
                <h3>Earnings</h3>
                <span className="hint">last 6 months</span>
              </div>
              <svg className="chart" viewBox="0 0 320 130" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="home-area" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="var(--acc)" stopOpacity=".34" />
                    <stop offset="1" stopColor="var(--acc)" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <line x1="0" y1="110" x2="320" y2="110" stroke="var(--line)" />
                <line x1="0" y1="70" x2="320" y2="70" stroke="var(--line)" strokeDasharray="3 5" />
                <line x1="0" y1="30" x2="320" y2="30" stroke="var(--line)" strokeDasharray="3 5" />
                <path d={chart.area} fill="url(#home-area)" />
                <path
                  d={chart.line}
                  fill="none"
                  stroke="var(--acc)"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx={chart.lastX} cy={chart.lastY} r="4" fill="var(--acc)" />
                <circle cx={chart.lastX} cy={chart.lastY} r="8" fill="var(--acc)" opacity=".18" />
              </svg>
              <div className="lbls">
                {home.earnings.map((p, i) => (
                  <span key={`${p.label}-${i}`}>{p.label}</span>
                ))}
              </div>
            </div>

            <div className="card">
              <div className="card-h">
                <h3>Recent</h3>
              </div>
              {home.recent.length === 0 ? (
                <p className="recent-empty">Nothing yet. New activity lands here.</p>
              ) : (
                <div className="rows">
                  {home.recent.map((a) => {
                    const amt = amountText(a, home.currency);
                    return (
                      <div className="row" key={a.id}>
                        <ActivityIcon tone={a.tone} />
                        <div className="t">
                          <b>{a.title}</b>
                          <div className="d">{a.detail}</div>
                        </div>
                        <span className={amt.className}>{amt.text}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
